import { createServerFn } from '@tanstack/react-start';
import { defineQuery } from 'groq';

import type {
  FooterLandingPagesQueryResult,
  HomePageQueryResult,
  LandingPageQueryResult,
  SitemapLandingPagesQueryResult,
} from '@/sanity.types';
import {
  getSectionAnchors,
  resolveHomePageLinkHref,
} from '@/utils/section-links';

import { createSanityClient } from './client';

export type LandingPage = NonNullable<LandingPageQueryResult>;
export type HomePage = NonNullable<HomePageQueryResult>;
export type LandingContent = Pick<
  LandingPage,
  'headerButton' | 'hero' | 'sections'
>;
export type LandingHeroPanel = LandingPage['hero']['panel'][number];
type LandingHeroAction = NonNullable<LandingPage['hero']['actions']>[number];
type LandingHeroLink = Extract<
  LandingHeroAction,
  { _type: 'landingHeroAction' }
>;
type LandingHeroWaitlistForm = Extract<
  LandingHeroAction,
  { _type: 'landingHeroWaitlistForm' }
>;
export type FooterLandingPage = FooterLandingPagesQueryResult[number];
export type ResolvedHeaderButton = { label: string; href: string };

export const getHeroWaitlistForm = (
  hero: LandingContent['hero'],
): LandingHeroWaitlistForm | undefined =>
  hero.actions?.find(
    (action): action is LandingHeroWaitlistForm =>
      action._type === 'landingHeroWaitlistForm',
  );

export const getHeroLinks = (hero: LandingContent['hero']): LandingHeroLink[] =>
  hero.actions?.filter(
    (action): action is LandingHeroLink => action._type === 'landingHeroAction',
  ) ?? [];

const landingSectionHeaderProjection = `
  anchorId,
  eyebrow,
  title,
  "description": coalesce(description, null),
  "footnote": coalesce(footnote, null),
`;

export const landingSectionProjection = `
  _key,
  _type,
  _type == "landingCardGridSection" => {
    ${landingSectionHeaderProjection}
    cards[] {
      _key,
      title,
      items
    }
  },
  _type == "landingFeatureCardsSection" => {
    ${landingSectionHeaderProjection}
    features[] {
      _key,
      title,
      body
    }
  },
  _type == "landingStepsSection" => {
    ${landingSectionHeaderProjection}
    steps[] {
      _key,
      title,
      body
    }
  },
  _type == "landingFaqSection" => {
    ${landingSectionHeaderProjection}
    faqs[] {
      _key,
      question,
      answer
    }
  },
  _type == "waitlist" => {
    "heading": coalesce(heading, null),
    "text": coalesce(text, null)
  },
  _type == "agentWaitlist" => {
    "heading": coalesce(heading, null),
    "text": coalesce(text, null)
  },
  _type == "compareSection" => {
    "heading": coalesce(heading, null),
    "text": coalesce(text, null)
  }
`;

const landingContentProjection = `
  seoTitle,
  seoDescription,
  headerButton {
    label,
    url
  },
  hero {
    eyebrow,
    title,
    description,
    actions[] {
      _key,
      _type,
      _type == "landingHeroAction" => {
        label,
        url,
        isPrimary
      },
      _type == "landingHeroWaitlistForm" => {
        buttonLabel
      }
    },
    "footnote": coalesce(footnote, null),
    panel[] {
      _key,
      _type,
      heading,
      footnote,
      _type == "landingHeroChecklistPanel" => {
        subheading,
        items
      },
      _type == "landingHeroComparisonPanel" => {
        badge,
        summaryLines,
        rows[] {
          _key,
          label,
          value
        },
        highlightLabel,
        highlightValue,
        "tagsLabel": coalesce(tagsLabel, null),
        "tags": coalesce(tags, [])
      },
      _type == "regionDeadlines" => {
        badge,
        summaryLines,
        selectLabel,
        regions[] {
          _key,
          name,
          opensOn,
          closesOn
        },
        reminderLink {
          upcomingLabel,
          openLabel,
          url
        }
      },
      _type == "compareWidget" => {
        "text": coalesce(text, null)
      },
      _type == "agentWaitlist" => {
        "text": coalesce(text, null)
      }
    }
  },
  sections[] {
    ${landingSectionProjection}
  }
`;

const landingPageQuery = defineQuery(`*[
  _type == "landingPage" &&
  slug.current == $slug
][0] {
  "id": _id,
  "updatedAt": _updatedAt,
  title,
  "slug": slug.current,
  ${landingContentProjection}
}`);

const homePageQuery = defineQuery(`*[
  _type == "homePage" &&
  _id == "homePage"
][0] {
  "id": _id,
  "updatedAt": _updatedAt,
  ${landingContentProjection}
}`);

const homeHeaderButtonQuery = defineQuery(`*[
  _type == "homePage" &&
  _id == "homePage"
][0] {
  headerButton {
    label,
    url
  },
  "sections": sections[] {
    _key,
    _type
  }
}`);

const sitemapLandingPagesQuery = defineQuery(`*[
  _type == "landingPage" &&
  defined(slug.current)
] | order(slug.current asc) {
  "slug": slug.current,
  "updatedAt": _updatedAt
}`);

const sitemapHomePageQuery = defineQuery(`*[
  _type == "homePage" &&
  _id == "homePage"
][0] {
  "updatedAt": _updatedAt
}`);

const footerLandingPagesQuery = defineQuery(`*[
  _type == "landingPage" &&
  showInFooter == true &&
  defined(slug.current) &&
  defined(footerLabel)
] | order(footerLabel asc) {
  "slug": slug.current,
  "label": footerLabel,
  audience
}`);

/** Public server function: it only reads one published document from Sanity. */
export const getLandingPage = createServerFn({ method: 'GET' })
  .validator((slug: unknown) => {
    if (typeof slug !== 'string') {
      throw new Error('Landing page slug must be a string');
    }

    const normalizedSlug = slug.trim();
    if (normalizedSlug.length === 0 || normalizedSlug.length > 96) {
      throw new Error('Landing page slug must be between 1 and 96 characters');
    }

    return normalizedSlug;
  })
  .handler(async ({ data: slug }): Promise<LandingPage | null> => {
    const client = createSanityClient();

    try {
      return await client.fetch(landingPageQuery, { slug });
    } catch (error) {
      console.error('Failed to load Sanity landing page', error);
      return null;
    }
  });

/** Public server function: it only reads one published document from Sanity. */
export const getHomePage = createServerFn({ method: 'GET' }).handler(
  async (): Promise<HomePage | null> => {
    const client = createSanityClient();

    try {
      return await client.fetch(homePageQuery);
    } catch (error) {
      console.error('Failed to load the Sanity home page', error);
      return null;
    }
  },
);

/**
 * Public server function: it only reads the published home page header
 * button, for pages that aren't built from Sanity landing content.
 */
export const getHomeHeaderButton = createServerFn({ method: 'GET' }).handler(
  async (): Promise<ResolvedHeaderButton | null> => {
    const client = createSanityClient();

    try {
      const homePage = await client.fetch(homeHeaderButtonQuery);

      if (!homePage?.headerButton) {
        return null;
      }

      return {
        label: homePage.headerButton.label,
        href: resolveHomePageLinkHref(
          homePage.headerButton.url,
          getSectionAnchors(homePage.sections),
        ),
      };
    } catch (error) {
      console.error('Failed to load the Sanity home page header button', error);
      return null;
    }
  },
);

/** Public server function: it only reads published navigation labels. */
export const getFooterLandingPages = createServerFn({ method: 'GET' }).handler(
  async (): Promise<FooterLandingPagesQueryResult> => {
    const client = createSanityClient();

    try {
      return await client.fetch(footerLandingPagesQuery);
    } catch (error) {
      console.error(
        'Failed to load Sanity landing pages for the footer',
        error,
      );
      return [];
    }
  },
);

export async function getSitemapHomeUpdatedAt(): Promise<string | null> {
  const client = createSanityClient();

  try {
    const homePage = await client.fetch<{ updatedAt: string } | null>(
      sitemapHomePageQuery,
    );

    return homePage?.updatedAt ?? null;
  } catch (error) {
    console.error('Failed to load the Sanity home page for the sitemap', error);
    return null;
  }
}

export async function getSitemapLandingPages(): Promise<SitemapLandingPagesQueryResult> {
  const client = createSanityClient();

  try {
    return await client.fetch(sitemapLandingPagesQuery);
  } catch (error) {
    console.error('Failed to load Sanity landing pages for the sitemap', error);
    return [];
  }
}
